import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Clock3, FileWarning, ShieldOff, Timer } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1593427995298-cad6731716d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBza3lzY3JhcGVyJTIwYXJjaGl0ZWN0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODk3NDA4NzB8MA&ixlib=rb-4.1.0&q=85";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.35 } },
};
const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] } },
};
const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

function TiltCard() {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setTilt({ x: (py - 0.5) * -10, y: (px - 0.5) * 12 });
  };

  return (
    <motion.div variants={fade} className="relative" style={{ perspective: 1200 }}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ x: 0, y: 0 })}
        data-testid="speed-comparison-card"
        className="glass-panel rounded-md p-6 sm:p-8 transition-transform duration-200 ease-out will-change-transform"
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
      >
        <p className="eyebrow mb-6">Time-to-capital index</p>
        <div className="space-y-5">
          <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500">Traditional bank</span>
              <Clock3 className="w-4 h-4 text-zinc-600" />
            </div>
            <p className="font-display text-2xl sm:text-3xl font-light text-zinc-500 line-through decoration-rose-500/60">
              45–90 days
            </p>
            <p className="text-xs text-zinc-600 mt-2 flex items-center gap-1.5">
              <FileWarning className="w-3.5 h-3.5" /> 37-page collateral file · personal guarantee
            </p>
          </div>
          <div className="border border-amber-500/40 rounded-sm p-4 bg-amber-500/[0.06] relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-500/20 blur-3xl rounded-full" />
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono-d tracking-[0.2em] uppercase text-amber-400">Forward Exspansion</span>
              <Timer className="w-4 h-4 text-amber-400" />
            </div>
            <p className="font-display text-3xl sm:text-4xl font-light text-zinc-100">4–24 hours</p>
            <p className="text-xs text-amber-200/70 mt-2 flex items-center gap-1.5">
              <ShieldOff className="w-3.5 h-3.5" /> Zero asset liens · revenue-based approval
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function KineticHero() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  return (
    <section ref={heroRef} id="top" className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="kinetic-hero">
      <motion.div className="absolute inset-0" style={{ y: bgY, opacity: bgOpacity }}>
        <img
          src={HERO_IMG}
          alt="Dark modern financial high-rise architecture at night"
          className="w-full h-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07090D]/70 via-[#07090D]/60 to-[#07090D]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(245,158,11,0.12),transparent_55%)]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.p variants={fade} className="eyebrow mb-8" data-testid="hero-eyebrow">
              Alternative commercial finance · Est. velocity
            </motion.p>
            <h1 className="font-display font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-7xl text-zinc-100" data-testid="hero-headline">
              <span className="block overflow-hidden pb-1">
                <motion.span variants={lineReveal} className="block">Banks move in weeks.</motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span variants={lineReveal} className="block">
                  We wire <span className="font-editorial italic text-amber-400">in hours.</span>
                </motion.span>
              </span>
            </h1>
            <motion.p variants={fade} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-400" data-testid="hero-subcopy">
              Forward Exspansion syndicates your revenue across 30+ institutional lenders — forcing a
              bidding war that funds your business before a bank finishes its first committee
              meeting. No collateral. No liens. No waiting.
            </motion.p>
            <motion.div variants={fade} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href="#pre-approval"
                data-testid="hero-cta-button"
                className="cta-glow group inline-flex items-center gap-3 bg-amber-500 hover:bg-amber-400 text-[#07090D] font-semibold text-base px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
              >
                Check Pre-Approval Status
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </a>
              <a
                href="#structure"
                data-testid="hero-secondary-link"
                className="text-sm font-mono-d tracking-[0.15em] uppercase text-zinc-400 hover:text-amber-400 border-b border-zinc-700 hover:border-amber-400 pb-1 transition-colors duration-300"
              >
                See the structure
              </a>
            </motion.div>
            <motion.div variants={fade} className="mt-14 flex flex-wrap gap-x-10 gap-y-4" data-testid="hero-stats">
              {[
                ["$340M+", "capital brokered"],
                ["4.2h", "avg wire time"],
                ["30+", "institutional lenders"],
              ].map(([v, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl font-light text-zinc-100">{v}</p>
                  <p className="text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500 mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <TiltCard />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
